import React, { useRef, useState } from "react";

import { TwoUpCardBlock, TwoUpCardsBlock as Value } from "@reactivated";

import { StreamField } from "@thelabnyc/thelabui/src/components/StreamField";

import { Themed } from "../../styles/themes";
import { RichTextStreamBlock } from "../RichTextStreamBlock";
import { SpacerBlock } from "../SpacerBlock";
import { TwoUpCard } from "./TwoUpCard";

import styles from "./index.module.scss";

/**
 * Coordinate overflow detection across sibling cards so they respond together.
 * Each card reports how much its content overflows (as a ratio: 1 = fits,
 * >1 = overflowing). This hook collects those ratios and exposes the biggest
 * one as `maxRatio`, so both cards can make the same UI adjustments.
 */
export const useOverflowGroup = () => {
    /** Per-card overflow ratios, keyed by index */
    const map = useRef(new Map<number, number>());
    const [maxRatio, setMaxRatio] = useState(1);

    /** Store a card's ratio and recalculate the group max */
    const updateRatio = (index: number, ratio: number) => {
        map.current.set(index, ratio);
        setMaxRatio(Math.max(...Array.from(map.current.values())));
    };

    return [maxRatio, updateRatio] as const;
};

const TwoUpCardGrid = ({ value }: { value: TwoUpCardBlock[] }) => {
    const [maxRatio, updateRatio] = useOverflowGroup();

    return (
        <div className={styles.grid}>
            {value.map((card, index) => (
                <TwoUpCard
                    key={index}
                    value={card}
                    maxRatio={maxRatio}
                    onOverflowChange={(ratio) => updateRatio(index, ratio)}
                />
            ))}
        </div>
    );
};

const components = {
    rich_text_stream: RichTextStreamBlock,
    spacer: SpacerBlock,
    cards: TwoUpCardGrid,
};

export const TwoUpCardsBlock = ({ value }: { value: Value }) => {
    if (!value.items) return <></>;

    return (
        <Themed theme={value.theme} tag="section">
            <StreamField
                tagName={React.Fragment}
                components={components}
                value={value.items}
            />
        </Themed>
    );
};
